/**
 * RequirementEditModal Component
 * Edit a requirement's fulfillment verdict (Met / Partially Met / Not Met)
 * + implementation description. Built on the shared FormModal primitive:
 * the two inputs are declarative fields; the read-only requirement info
 * renders in the header (`headerExtra`).
 */

import FormModal from '../../../components/ui/FormModal/FormModal';
import styles from './RequirementEditModal.module.css';

const FIELDS = [
  {
    name: 'fulfillment_status',
    type: 'radio',
    label: 'Fulfillment Status',
    options: [
      { value: 'fulfilled', label: 'Met' },
      { value: 'partially_fulfilled', label: 'Partially Met' },
      { value: 'not_fulfilled', label: 'Not Met' },
    ],
  },
  {
    name: 'implementation_description',
    type: 'textarea',
    label: 'Implementation Description',
    required: true,
    rows: 5,
    placeholder:
      'Describe how this requirement is fulfilled by the implemented controls...',
    helpText:
      'Provide evidence or explanation of how the requirement is addressed.',
  },
];

// Normalize a stored/backend fulfillment value (incl. the uppercase
// enhanced-prompt forms) onto one of the three radio option values.
const normaliseFulfillment = (raw) => {
  const v = (raw || '').toLowerCase();
  if (v.includes('not')) return 'not_fulfilled';
  if (v.includes('partial')) return 'partially_fulfilled';
  return 'fulfilled';
};

const RequirementEditModal = ({
  isOpen,
  onClose,
  requirement,
  controlId,
  onSave,
}) => {
  if (!isOpen || !requirement) return null;

  const initialValues = {
    fulfillment_status: normaliseFulfillment(requirement.fulfillment_status),
    implementation_description: requirement.implementation_description || '',
  };

  // Throwing bubbles to FormModal's inline submit error and keeps the modal
  // open; on success FormModal calls onClose itself.
  const handleSubmit = async (values) => {
    await onSave({
      fulfillment_status: values.fulfillment_status,
      implementation_description: (values.implementation_description || '').trim(),
    });
  };

  return (
    <FormModal
      title="Edit Requirement Evaluation"
      headerExtra={
        <div className={styles.requirementInfo}>
          <div className={styles.requirementId}>{requirement.req_id}</div>
          <div className={styles.requirementStatement}>
            {requirement.req_statement || requirement.req_title}
          </div>
          {controlId && <div className={styles.controlRef}>Control: {controlId}</div>}
        </div>
      }
      fields={FIELDS}
      initialValues={initialValues}
      submitLabel="Save Changes"
      submittingLabel="Saving..."
      onSubmit={handleSubmit}
      onClose={onClose}
      maxWidth={620}
    />
  );
};

export default RequirementEditModal;
